/**
 * Step 1 — Chatwork API token を入力 + 検証
 *
 * 設計書: docs/chatwork_killer/03_product_strategy.md §3.2 (Wizard step 1)
 * Issue:   skill-bridge/media_app#6000
 *
 * UX:
 *   - 入力フィールドはマスク (type=password)、目玉アイコンで表示切替
 *   - "API トークンの取得手順" を折り畳みで提示
 *   - 検証中は spinner、成功時は ✓ + organization 名表示、失敗時は error message
 */

import { useState, useId, type FormEvent } from "react";
import { Eye, EyeOff, ShieldCheck, ExternalLink, Loader2 } from "lucide-react";
import type { ChatworkAccountInfo } from "./types";
import { MigrationApi, MigrationApiError } from "./api";

export interface ChatworkConnectStepProps {
  api: MigrationApi;
  onConnected: (job_id: string, account: ChatworkAccountInfo) => void;
}

const HELP_URL =
  "https://help.chatwork.com/hc/ja/articles/115000172402-API-Token%E3%82%92%E5%8F%96%E5%BE%97%E3%81%99%E3%82%8B";

export function ChatworkConnectStep(props: ChatworkConnectStepProps) {
  const [token, setToken] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token.trim()) {
      setError("Chatwork API トークンを入力してください。");
      return;
    }
    setBusy(true);
    try {
      const res = await props.api.connect(token.trim());
      props.onConnected(res.job_id, res.chatwork);
    } catch (e) {
      if (e instanceof MigrationApiError) {
        if (e.code === "CHATWORK_INVALID_TOKEN") {
          setError("Chatwork から拒否されました。トークンが正しいか確認してください。");
        } else if (e.code === "VALIDATION_ERROR") {
          setError("トークンの形式が無効です（半角英数のみ／20文字以上）。");
        } else if (e.code === "AUTH_MISSING") {
          setError("エールチャット へのログインが必要です。");
        } else {
          setError(e.message);
        }
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mx-auto w-full max-w-xl rounded-lg border border-border-subtle bg-canvas p-8 shadow-md"
      aria-labelledby="connect-title"
      data-testid="chatwork-connect-step"
    >
      <header className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">Step 1 / 4</p>
        <h2 id="connect-title" className="mt-1 text-2xl font-bold text-fg-primary">
          Chatwork へ接続
        </h2>
        <p className="mt-2 text-base text-fg-secondary">
          Chatwork の <strong>API トークン</strong>を入力し、取得できるルーム・メッセージ・タスク・ファイルを選んで取り込みます。
        </p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-success-bg px-3 py-1 text-sm font-medium text-success">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          無償移行支援 (¥0) — 移行サポート費もかかりません
        </div>
      </header>

      <p className="mb-4 text-sm text-fg-secondary">Chatwork API の制約により、メッセージは各ルームの取得可能な直近最大 100 件が対象です。全履歴の移行は保証されません。次の画面で対象件数を確認してください。</p>
      <form onSubmit={onSubmit} className="space-y-4" data-testid="chatwork-connect-form">
        <div>
          <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-fg-primary">
            Chatwork API トークン
          </label>
          <div className="relative">
            <input
              id={inputId}
              type={show ? "text" : "password"}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="例: abcdef0123456789..."
              className="h-11 w-full rounded-md border border-border-default bg-canvas px-3 pr-12 font-mono text-base text-fg-primary placeholder:text-fg-subtle focus-visible:shadow-focus"
              data-testid="chatwork-token-input"
              aria-invalid={Boolean(error) || undefined}
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-fg-muted hover:text-fg-primary"
              aria-label={show ? "トークンを隠す" : "トークンを表示"}
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-1 text-xs text-fg-muted">
            トークンはサーバー側で暗号化 (AES-256) して保存します。エールチャット 以外には共有されません。
          </p>
          {error ? (
            <p
              role="alert"
              data-testid="chatwork-connect-error"
              className="mt-2 text-sm text-danger"
            >
              {error}
            </p>
          ) : null}
        </div>

        <details className="rounded-md border border-border-subtle bg-surface-base p-3 text-sm">
          <summary className="cursor-pointer font-medium text-fg-primary">
            API トークンの取得手順
          </summary>
          <ol className="mt-2 list-decimal pl-5 text-fg-secondary">
            <li>Chatwork にログインし、右上のアカウント名 → 「API 設定」を開く</li>
            <li>本人認証パスワードを入力し、「API Token」を発行する</li>
            <li>表示されたトークンをコピーしてここに貼り付け</li>
          </ol>
          <a
            href={HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700"
          >
            Chatwork ヘルプ <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </details>

        <button
          type="submit"
          disabled={busy}
          aria-busy={busy || undefined}
          data-testid="chatwork-connect-submit"
          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand-600 px-5 font-medium text-fg-inverse transition-colors duration-150 hover:bg-brand-700 disabled:bg-fg-subtle"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              検証中...
            </>
          ) : (
            "次へ — 移行内容を確認"
          )}
        </button>
      </form>
    </section>
  );
}
